import styled, { css } from "styled-components";

import {
  STACKED_CARD_CONTROL_CLASS,
  STACKED_CARD_FIXED_ATTR,
  STACKED_CARD_RADIUS,
  STACKED_CARD_ROLE_PADDING,
  STACKED_CARD_WIDTH,
  StackedCardRowDirection,
  StackedCardRowRole,
} from "./constants";
import { StackedCardResizeAxis } from "./resizePolicy";
import { BoxSizingStyle } from "../BoxSizingStyle";

/**
 * Theming hooks.
 *
 * Every colour a StackedCard part needs is read through a `--sc-*` custom
 * property with a built-in fallback, so the component looks right standing on
 * its own and a host surface can still retint it by declaring the variable on
 * any ancestor. No rgba/hex is baked into a rule; data colours (a swatch value,
 * a priority colour) are always passed in by the caller.
 */
const SELECTION_RING = "var(--sc-selection-ring, var(--color-primary-500))";
const SCRIM = "var(--sc-scrim, rgba(0, 0, 0, 0.55))";
const SCRIM_STRONG = "var(--sc-scrim-strong, rgba(0, 0, 0, 0.72))";
const SCRIM_FG = "var(--sc-scrim-fg, #ffffff)";

/** Selector for anything carrying the control class. */
const CONTROL = `.${STACKED_CARD_CONTROL_CLASS}`;

/**
 * The one reveal rule the whole subsystem shares: controls are hidden until the
 * card (or the cover) is hovered OR holds focus. The focus path is what keeps
 * them reachable by keyboard instead of mouse-only.
 */
const revealControls = css`
  ${CONTROL} {
    opacity: 0;
    transition: opacity 120ms ease;
  }

  &:hover ${CONTROL}, &:focus-within ${CONTROL} {
    opacity: 1;
  }
`;

/* ------------------------------------------------------------------ */
/* Stack — the vertical column. A card is a Stack of rows.             */
/* ------------------------------------------------------------------ */

export const StyledStackedCardStack = styled.div`
  ${BoxSizingStyle}

  position: relative;
  display: flex;
  flex-direction: column;
`;
StyledStackedCardStack.displayName = "StyledStackedCardStack";

/* ------------------------------------------------------------------ */
/* Row — the only layout primitive, differentiated by `role`.          */
/* ------------------------------------------------------------------ */

interface IStyledRowProps {
  $role: StackedCardRowRole;
  $bleed?: boolean;
  $direction: StackedCardRowDirection;
  $background?: string;
}

export const StyledStackedCardRow = styled.div<IStyledRowProps>`
  ${BoxSizingStyle}

  position: relative;
  display: flex;
  flex-direction: ${({ $direction }) => $direction};
  align-items: ${({ $direction }) =>
    $direction === "column" ? "stretch" : "center"};
  gap: ${({ $direction }) => ($direction === "column" ? "4px" : "8px")};

  /* bleed overrides any role and runs the row edge to edge. */
  padding: ${({ $role, $bleed }) =>
    $bleed ? "0" : STACKED_CARD_ROLE_PADDING[$role]};

  ${({ $background }) =>
    $background &&
    css`
      background: ${$background};
    `}

  /* Distribution (row direction only): fixed-width children keep their width,
     the rest share what remains evenly; drop an empty spacer to push items. */
  ${({ $direction }) =>
    $direction === "row" &&
    css`
      > * {
        flex: 1 1 0;
        min-width: 0;
      }

      > [${STACKED_CARD_FIXED_ATTR}] {
        flex: 0 0 auto;
      }
    `}
`;
StyledStackedCardRow.displayName = "StyledStackedCardRow";

/* ------------------------------------------------------------------ */
/* Cover — a bleed row that holds media.                               */
/* ------------------------------------------------------------------ */

/**
 * The media box reserves its height with `aspect-ratio` so the image (or the
 * skeleton standing in for it) never reflows the card when it arrives.
 */
export const StyledStackedCardCoverMedia = styled.div<{ $aspectRatio: string }>`
  position: relative;
  width: 100%;
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio};
  overflow: hidden;

  ${revealControls}
`;
StyledStackedCardCoverMedia.displayName = "StyledStackedCardCoverMedia";

/* The fill (an <img>, an inline <svg> wrapper, or a solid-colour box) is laid
   over the reserved box and made to cover it. */
export const StyledStackedCardCoverFill = styled.div`
  position: absolute;
  inset: 0;

  > * {
    width: 100%;
    height: 100%;
  }

  > img {
    display: block;
    object-fit: cover;
  }

  /* Media must never be natively draggable. An <img> is a browser drag source
     by default, so grabbing the cover would start an image drag and swallow the
     mousemove/mouseup the HOST's drag depends on — the card would stay put and
     then jump on the next pointer move. The host owns dragging; the cover must
     not compete with it. */
  > img,
  > svg {
    -webkit-user-drag: none;
    user-select: none;
    pointer-events: none;
  }
`;
StyledStackedCardCoverFill.displayName = "StyledStackedCardCoverFill";

export const StyledStackedCardCoverCenter = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;
StyledStackedCardCoverCenter.displayName = "StyledStackedCardCoverCenter";

export const StyledStackedCardCoverTopRight = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 6px;
`;
StyledStackedCardCoverTopRight.displayName = "StyledStackedCardCoverTopRight";

/**
 * Collapsed cover — the media box gives way to a thin strip. The strip IS the
 * expand affordance: the whole bar is a button and a chevron hint is revealed
 * on hover or focus. It reserves no height of its own; the caller's
 * `collapsedStrip` (an ~8px colour bar, a favicon + domain row) sets how tall
 * it reads.
 */
export const StyledStackedCardCollapsedStrip = styled.button`
  ${BoxSizingStyle}

  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 16px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  text-align: left;

  ${CONTROL} {
    opacity: 0;
    transition: opacity 120ms ease;
  }

  &:hover ${CONTROL}, &:focus-visible ${CONTROL} {
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid ${SELECTION_RING};
    outline-offset: -2px;
  }
`;
StyledStackedCardCollapsedStrip.displayName = "StyledStackedCardCollapsedStrip";

/* The expand chevron sits at the right, vertically centred, so it reads the
   same whether the strip is a hairline colour bar or a taller favicon row. */
export const StyledStackedCardStripExpand = styled.span`
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  display: inline-flex;
  width: 16px;
  height: 16px;
  color: var(--color-theme-600);

  svg {
    width: 14px;
    height: 14px;
  }
`;
StyledStackedCardStripExpand.displayName = "StyledStackedCardStripExpand";

/* ------------------------------------------------------------------ */
/* Cover chrome — scrim-backed controls that sit over media            */
/* ------------------------------------------------------------------ */

/** Centered media control (a play button). The caller drops the glyph inside. */
export const StyledStackedCardPlayBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${SCRIM};
  color: ${SCRIM_FG};

  svg {
    fill: currentColor;
    width: 20px;
    height: 20px;
    margin-left: 2px;
  }
`;
StyledStackedCardPlayBadge.displayName = "StyledStackedCardPlayBadge";

/** Top-right cover-management button (collapse, remove …). */
export const StyledStackedCardCoverButton = styled.button`
  ${BoxSizingStyle}

  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  color: ${SCRIM_FG};
  background: ${SCRIM};

  svg {
    width: 16px;
    height: 16px;
    display: block;
  }

  &:hover {
    background: ${SCRIM_STRONG};
  }
`;
StyledStackedCardCoverButton.displayName = "StyledStackedCardCoverButton";

/* ------------------------------------------------------------------ */
/* Resize (opt-in): handle on hover, the card only reports deltas      */
/* ------------------------------------------------------------------ */

/* The grip is a focusable separator, so it needs a focus ring of its own — a
   keyboard user has no pointer to tell them where they are. */
export const StyledStackedCardResizeHandle = styled.span<{
  $axis: StackedCardResizeAxis;
}>`
  position: absolute;
  right: 2px;
  bottom: 2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 4px;
  cursor: ${({ $axis }) => ($axis === "width" ? "ew-resize" : "nwse-resize")};
  color: var(--color-theme-600);
  z-index: 2;

  &:hover {
    color: var(--color-theme-900);
  }

  &:focus-visible {
    outline: 2px solid ${SELECTION_RING};
    outline-offset: 1px;
    color: var(--color-theme-900);
  }

  svg {
    display: block;
  }
`;
StyledStackedCardResizeHandle.displayName = "StyledStackedCardResizeHandle";

/* ------------------------------------------------------------------ */
/* The shell                                                           */
/* ------------------------------------------------------------------ */

export interface IStyledStackedCardProps {
  $selected?: boolean;
  $disabled?: boolean;
  $pending?: boolean;
  $isDragSource?: boolean;
  $isDragPreview?: boolean;
  $entered?: boolean;
  $forceHover?: boolean;
  $priorityColor?: string;
}

const hoverStyle = css`
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  border-color: var(--color-theme-500);
`;

export const StyledStackedCard = styled.div<IStyledStackedCardProps>`
  ${BoxSizingStyle}

  position: relative;
  display: flex;
  flex-direction: column;
  width: ${STACKED_CARD_WIDTH}px;
  border-radius: ${STACKED_CARD_RADIUS}px;
  background-color: var(--page-paper-main);
  border: 1px solid var(--border-primary);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: box-shadow 150ms ease, border-color 150ms ease;

  ${revealControls}

  &:hover {
    ${hoverStyle}
  }

  &:focus-visible {
    outline: none;
    ${hoverStyle}
    box-shadow: 0 0 0 2px ${SELECTION_RING};
  }

  /* Forced hover — for docs, snapshots and "this is what hover looks like"
     demos. The doubled ampersand gives it the specificity to beat the cover's
     own reveal rule. */
  ${({ $forceHover }) =>
    $forceHover &&
    css`
      ${hoverStyle}
      && ${CONTROL} {
        opacity: 1;
      }
    `}

  ${({ $priorityColor }) =>
    $priorityColor &&
    css`
      &::before {
        content: "";
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        width: 4px;
        background: ${$priorityColor};
        z-index: 2;
      }
    `}

  /* Selection outranks hover: a selected card must keep its ring while the
     pointer is over it — which, right after the click that selected it, is
     always. The doubled ampersand buys the specificity to beat the hover rule
     above (same reason the entered state below does it). */
  ${({ $selected }) =>
    $selected &&
    css`
      && {
        border-color: ${SELECTION_RING};
        box-shadow: 0 0 0 2px ${SELECTION_RING};
      }
    `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
    `}

  /* Pending / optimistic — created or moved, not yet confirmed by the server.
     Dimmed, non-interactive and NOT draggable, but distinct from disabled: a
     transient in-flight state, not a policy. */
  ${({ $pending }) =>
    $pending &&
    css`
      opacity: 0.6;
      pointer-events: none;
      cursor: progress;
    `}

  /* Drag SOURCE — full-size outline placeholder. Content is hidden but keeps
     its box (visibility, not display) so the card holds identical dimensions
     and the surrounding cards never move. */
  ${({ $isDragSource }) =>
    $isDragSource &&
    css`
      background: transparent;
      border: 1px dashed var(--color-theme-500);
      box-shadow: none;

      &:hover {
        box-shadow: none;
        border-color: var(--color-theme-500);
      }

      > * {
        visibility: hidden;
      }
    `}

  /* Drag PREVIEW — the 1:1 copy the host floats in its drag layer. Elevated
     shadow ONLY: no rotate, no scale, no translate. */
  ${({ $isDragPreview }) =>
    $isDragPreview &&
    css`
      box-shadow: 0 12px 28px rgba(0, 0, 0, 0.24);
      cursor: grabbing;
    `}

  /* Entered (focus model) — focus has moved inside, onto an inner control. */
  ${({ $entered }) =>
    $entered &&
    css`
      && {
        border-color: ${SELECTION_RING};
        box-shadow: inset 0 0 0 2px ${SELECTION_RING},
          0 0 0 2px ${SELECTION_RING};
      }
    `}
`;
StyledStackedCard.displayName = "StyledStackedCard";
